/**
 * Phone pairing: a QR to scan and the same code in digits to type.
 *
 * Both are offered because the QR fails often enough to matter — a cracked
 * screen, a camera app that refuses http:// on a LAN address, a phone whose
 * scanner is buried in a menu. The digits always work.
 *
 * The QR renders to a canvas locally; nothing is sent to a QR service.
 */
import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Check, RefreshCw, Smartphone } from "lucide-react";
import type { DeviceStatus } from "@exam/shared";
import { Badge, Button } from "@/components/ui";
import { cn } from "@/lib/cn";

export function PairingPanel({
  pairCode,
  joinUrl,
  phone,
  phoneAlive,
  onRotate,
  rotating,
}: {
  pairCode: string | null;
  joinUrl: string | null;
  phone: DeviceStatus | undefined;
  phoneAlive: boolean;
  onRotate: () => void;
  rotating: boolean;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <QrPanel value={joinUrl} />

        <div className="min-w-0 flex-1">
          <p className="text-xs text-ink-muted">
            On your phone, scan this code — or open the address below and enter
            the pairing code.
          </p>

          <dl className="mt-3 flex flex-col gap-2">
            <div>
              <dt className="text-2xs text-ink-faint">Address</dt>
              <dd className="mt-0.5 rounded-md border border-border bg-surface-sunken px-2 py-1 font-mono text-2xs break-all text-ink">
                {joinUrl ?? "…"}
              </dd>
            </div>
            <div>
              <dt className="text-2xs text-ink-faint">Pairing code</dt>
              <dd className="tabular mt-0.5 text-xl font-semibold tracking-[0.2em] text-ink">
                {pairCode ?? "······"}
              </dd>
            </div>
          </dl>

          <Button
            className="mt-3"
            size="sm"
            variant="ghost"
            icon={<RefreshCw />}
            onClick={onRotate}
            disabled={rotating}
          >
            {rotating ? "Getting a new code…" : "Get a new code"}
          </Button>
        </div>
      </div>

      <div
        className={cn(
          "flex items-center gap-2 rounded-md border px-3 py-2",
          phoneAlive
            ? "border-answered/30 bg-answered-soft"
            : "border-border bg-surface-sunken",
        )}
      >
        {phoneAlive ? (
          <Check className="size-3.5 shrink-0 text-answered" />
        ) : (
          <Smartphone className="size-3.5 shrink-0 text-ink-faint" />
        )}

        <span className="text-xs text-ink">
          {phoneAlive
            ? "Phone connected and sending video."
            : phone?.paired
              ? "Phone paired but not responding. Wake it and keep the page open."
              : "Waiting for your phone to connect…"}
        </span>

        {phone?.paired ? (
          <Badge tone={phoneAlive ? "answered" : "review"} className="ml-auto">
            {phoneAlive ? "Live" : "No signal"}
          </Badge>
        ) : null}
      </div>
    </div>
  );
}

function QrPanel({ value }: { value: string | null }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !value) return;

    QRCode.toCanvas(canvas, value, {
      width: 168,
      margin: 1,
      errorCorrectionLevel: "M",
      color: {
        // Design tokens, resolved here because the canvas cannot use CSS vars.
        dark: "#1c1917",
        light: "#ffffff",
      },
    }).then(
      () => setFailed(false),
      () => setFailed(true),
    );
  }, [value]);

  return (
    <div className="shrink-0 rounded-md border border-border bg-surface p-2">
      {failed || !value ? (
        <div className="flex size-[168px] items-center justify-center">
          <p className="px-3 text-center text-2xs text-ink-faint">
            Could not draw the QR code. Use the address and code instead.
          </p>
        </div>
      ) : (
        <canvas
          ref={canvasRef}
          width={168}
          height={168}
          aria-label="QR code linking to the phone camera page"
        />
      )}
    </div>
  );
}
